import React, { memo } from 'react';
import { Tooltip, CircularProgress } from '@mui/material';
import {
  Container,
  MenuButton,
  TextButton,
  Contents,
  IconBox,
  Label,
} from './styles';
import { UnifiedButtonProps, ButtonVariant } from './types';

/**
 * Drop‑in replacement for legacy MenuButton / TextButton(+Tooltip).
 * Recognises old prop names so existing code keeps working.
 */
const UnifiedButton = (
  rawProps: UnifiedButtonProps & { isDisabled?: boolean; isLoading?: boolean }
) => {
  /* ---------- map legacy aliases ---------- */
  const disabled  = rawProps.disabled  ?? rawProps.isDisabled ?? false;
  const inProcess = rawProps.inProcess ?? rawProps.isLoading  ?? false;

  /* ---------- defaults ---------- */
  const {
    icon,
    label,
    onClick,
    isActive            = false,
    margin              = '2px 4px',   /* legacy default */
    tooltip,
    tooltipPlacement    = 'right',

    /* data attributes */
    'data-tour-id': dataTourId,
    'data-testid': dataTestId,

    /* text */
    width     = 100,
    height    = 40,
    fontSize  = 14,
    iconRight = false,
    isMuted   = false,
    isWarning = false,

    /* menu */
    size = 32,

    /* explicit override */
    variant: variantProp,
  } = rawProps;

  const isBlocked = disabled || inProcess;
  const variant: ButtonVariant = variantProp ?? (label ? 'text' : 'menu');
  
  // Prepare data attributes to pass through
  const dataAttributes: Record<string, string> = {};
  if (dataTourId) dataAttributes['data-tour-id'] = dataTourId;
  if (dataTestId) dataAttributes['data-testid'] = dataTestId;

  /* ---------- render helpers ---------- */
  const renderTextContents = () => (
    <Contents disabled={isBlocked}>
      {/* left icon / spinner */}
      {(!iconRight || inProcess) && (
        <IconBox $disabled={disabled} $active={isActive}>
          {inProcess ? <CircularProgress size={fontSize} /> : icon}
        </IconBox>
      )}
      {label && <Label fontSize={fontSize}>{label}</Label>}
      {/* right icon */}
      {icon && iconRight && !inProcess && (
        <IconBox $disabled={disabled} $active={isActive}>
          {icon}
        </IconBox>
      )}
    </Contents>
  );

  const inner =
    variant === 'menu' ? (
      <MenuButton
        size={size}
        $active={isActive}
        disabled={isBlocked}
        onClick={isBlocked ? undefined : onClick}
        {...dataAttributes}
      >
        {inProcess ? <CircularProgress size={size * 0.6} /> : icon}
      </MenuButton>
    ) : (
      <TextButton
        role="button"
        width={width}
        height={height}
        isWarning={isWarning}
        isMuted={isMuted}
        disabled={isBlocked}
        $active={isActive}
        onClick={isBlocked ? undefined : onClick}
        {...dataAttributes}
      >
        {renderTextContents()}
      </TextButton>
    );

  /* ---------- tooltip wrapper ---------- */
  const wrapped = tooltip ? (
    <Tooltip title={tooltip} placement={tooltipPlacement} arrow>
      {/* span avoids MUI complaining about disabled elements */}
      <span>{inner}</span>
    </Tooltip>
  ) : (
    inner
  );

  return <Container margin={margin}>{wrapped}</Container>;
};

export default memo(UnifiedButton);
